const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const ZOOM_SHELL_NAMESPACE = "zoom.meeting.shell" as const;

export const ZOOM_SHELL_MESSAGE_VERSION = 1 as const;

export type ZoomShellStatus =
  | "initializing"
  | "ready"
  | "joining"
  | "joined"
  | "in-meeting"
  | "leaving"
  | "left"
  | "error";

export interface ZoomShellStatusPayload {
  status: ZoomShellStatus;
  /**
   * Millisecond timestamp generated by the sender.
   */
  timestamp: number;
  /**
   * Optional human-readable context (for logging or debugging).
   */
  message?: string;
  /**
   * Optional metadata bag for additional structured information.
   */
  metadata?: Record<string, unknown>;
}

export interface ZoomShellErrorPayload {
  code: string;
  message: string;
  recoverable?: boolean;
  data?: Record<string, unknown>;
}

export interface ZoomShellJoinContext {
  /**
   * Optional indicator of who triggered the join (e.g. "student", "instructor").
   */
  actor?: string;
  /**
   * Optional source identifier (e.g. page route, feature flag).
   */
  source?: string;
}

export interface ZoomJoinMeetingCommandPayload {
  signature: string;
  meetingNumber: string;
  userName: string;
  sdkKey?: string;
  password?: string;
  userEmail?: string;
  customerKey?: string;
  tk?: string;
  zak?: string;
  /**
    * Optional locale override passed from host app.
    * Should be a Zoom-supported locale code (e.g. "en-US").
    */
  language?: string;
  /**
   * Optional meeting context metadata used only for analytics/logging.
   */
  context?: ZoomShellJoinContext;
}

export interface ZoomLeaveMeetingCommandPayload {
  reason?: string;
}

export interface ZoomPingCommandPayload {
  timestamp: number;
}

export interface ZoomShellConfigurationPayload {
  language?: string;
  /**
   * Optional override for the Meeting SDK Client View leave URL.
   * When omitted, the shell will fall back to the current window location.
   */
  leaveUrl?: string;
  /**
   * If true, shell should automatically reconnect after transient network errors.
   */
  autoReconnect?: boolean;
  /**
   * List of allowed parent origins (exact match) that the shell will accept messages from.
   * If omitted, the shell trusts the first origin it receives a message from.
   */
  allowedOrigins?: string[];
}

export type ZoomShellEnvelope<
  TType extends string,
  TPayload
> = Readonly<{
  namespace: typeof ZOOM_SHELL_NAMESPACE;
  version: typeof ZOOM_SHELL_MESSAGE_VERSION;
  type: TType;
  payload: TPayload;
}>;

export type ZoomShellCommandEnvelope =
  | ZoomShellEnvelope<"JOIN_MEETING", ZoomJoinMeetingCommandPayload>
  | ZoomShellEnvelope<"LEAVE_MEETING", ZoomLeaveMeetingCommandPayload>
  | ZoomShellEnvelope<"END_MEETING", ZoomLeaveMeetingCommandPayload>
  | ZoomShellEnvelope<"PING", ZoomPingCommandPayload>
  | ZoomShellEnvelope<"CONFIGURE", ZoomShellConfigurationPayload>;

export type ZoomShellEventEnvelope =
  | ZoomShellEnvelope<"STATUS", ZoomShellStatusPayload>
  | ZoomShellEnvelope<"ERROR", ZoomShellErrorPayload>;

export type ZoomShellMessage = ZoomShellCommandEnvelope | ZoomShellEventEnvelope;

export const createZoomShellEnvelope = <TType extends string, TPayload>(
  type: TType,
  payload: TPayload
): ZoomShellEnvelope<TType, TPayload> => ({
  namespace: ZOOM_SHELL_NAMESPACE,
  version: ZOOM_SHELL_MESSAGE_VERSION,
  type,
  payload
});

export const isZoomShellEnvelope = (
  value: unknown
): value is ZoomShellEnvelope<string, unknown> => {
  if (!isRecord(value)) {
    return false;
  }

  if (value.namespace !== ZOOM_SHELL_NAMESPACE) {
    return false;
  }

  if (value.version !== ZOOM_SHELL_MESSAGE_VERSION) {
    return false;
  }

  if (typeof value.type !== "string") {
    return false;
  }

  return "payload" in value;
};

const commandTypes = new Set<ZoomShellCommandEnvelope["type"]>([
  "JOIN_MEETING",
  "LEAVE_MEETING",
  "END_MEETING",
  "PING",
  "CONFIGURE"
]);

export const isZoomShellCommand = (
  value: unknown
): value is ZoomShellCommandEnvelope =>
  isZoomShellEnvelope(value) && commandTypes.has(value.type as never);

const eventTypes = new Set<ZoomShellEventEnvelope["type"]>(["STATUS", "ERROR"]);

export const isZoomShellEvent = (
  value: unknown
): value is ZoomShellEventEnvelope =>
  isZoomShellEnvelope(value) && eventTypes.has(value.type as never);

export const assertUnreachable = (value: never): never => {
  throw new Error(`Unexpected value: ${String(value)}`);
};
